import { useState } from 'react';
import { Badge, Dropdown } from './ui';
import { useNotifications } from '../hooks';
import { useT } from '../i18n';

/**
 * Campana de notificaciones.
 *
 * Es un **panel**, no un menú: contiene títulos, textos y botones de «marcar
 * como leída», no una lista de acciones equivalentes. Declararlo `role="menu"`
 * haría que un lector de pantalla anunciara un menú y luego no encontrara
 * ninguna opción dentro. Se recorre con Tab, como cualquier contenido.
 */
export function NotificationBell() {
  const [recarga, setRecarga] = useState(0);
  const { data, markAllRead, markRead } = useNotifications(recarga);
  const t = useT();

  const notificaciones = data?.notifications ?? [];
  const sinLeer = notificaciones.filter((n) => !n.readAt).length;

  // El número va en el nombre accesible: el punto rojo no dice cuántas son, y
  // quien no lo ve necesita esa cifra tanto como quien sí.
  const etiqueta =
    sinLeer > 0
      ? `${t('nav.notifications')}, ${t('notifications.unreadCount', { count: sinLeer })}`
      : t('nav.notifications');

  return (
    <Dropdown
      kind="panel"
      label={etiqueta}
      trigger={() => (
        <span className="relative grid h-9 w-9 place-items-center rounded-xl text-muted transition hover:bg-surface-2/60 hover:text-content">
          <span aria-hidden="true">🔔</span>
          {sinLeer > 0 ? (
            <span
              aria-hidden="true"
              className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-danger ring-2 ring-background"
            />
          ) : null}
        </span>
      )}
    >
      {() => (
        <div className="w-80">
          <div className="flex items-center justify-between border-b border-line/60 px-3 py-2">
            <span className="text-sm font-medium">{t('nav.notifications')}</span>
            {sinLeer > 0 ? (
              <button
                type="button"
                className="min-h-[1.5rem] rounded-lg px-1 text-xs text-primary-soft hover:underline"
                onClick={async () => {
                  await markAllRead();
                  setRecarga((n) => n + 1);
                }}
              >
                {t('notifications.markAllRead')}
              </button>
            ) : null}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notificaciones.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted">{t('notifications.empty')}</p>
            ) : (
              notificaciones.slice(0, 12).map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={async () => {
                    if (!n.readAt) await markRead(n.id);
                    setRecarga((count) => count + 1);
                  }}
                  className="flex w-full flex-col gap-1 border-b border-line/40 px-3 py-2.5 text-left last:border-0 hover:bg-surface-2/60"
                >
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{n.title}</span>
                    {n.readAt ? null : <Badge tone="primary">{t('notifications.new')}</Badge>}
                  </span>
                  {n.message ? (
                    <span className="line-clamp-2 text-xs text-muted">{n.message}</span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </Dropdown>
  );
}
